/**
 * AIGeneratedBadge — EU AI Act Art. 50(1) disclosure label.
 *
 * Renders a small "auto-generated" pill next to LLM output so users see
 * clear, contemporaneous notice that the text was not written by a
 * human. Wording deliberately avoids "AI"/"KI"/"ИИ" per
 * docs/04-tone-of-voice.md — "auto" / "Auto" / "автоматически" are
 * clear enough for disclosure without breaching the brand voice.
 *
 * Mark every model-generated string in the athlete UI with this badge.
 * If the surrounding text was authored by a coach or a deterministic
 * template, do NOT render this badge.
 */
import { useTranslation } from "react-i18next";
import { Sparkle } from "@phosphor-icons/react";

export type AIGeneratedBadgeProps = {
  /** Optional override for the tooltip label (e.g. "Updated 2 minutes ago"). */
  tooltip?: string;
  /** Size variant — sm in tight rows, md for standalone callouts. */
  size?: "sm" | "md";
};

export function AIGeneratedBadge({ tooltip, size = "sm" }: AIGeneratedBadgeProps) {
  const { t } = useTranslation("common");
  const px = size === "md" ? "px-2 py-1 text-[11px]" : "px-1.5 py-0.5 text-[10px]";
  const iconSize = size === "md" ? 11 : 9;
  return (
    <span
      data-testid="ai-generated-badge"
      title={tooltip || t("ai_badge_tooltip", "Сформировано автоматически на основе ваших данных")}
      className={`inline-flex items-center gap-1 rounded-[3px] bg-fg-muted/15 font-semibold uppercase tracking-wider text-fg-muted ${px}`}
    >
      <Sparkle size={iconSize} aria-hidden="true" />
      <span>{t("ai_badge_label", "Авто")}</span>
    </span>
  );
}
